<template>
  <div class="overlay-loading" v-show="isWaitingForServer"></div>
  <div class="cashout-actions">
    <h2>Select Cashout game</h2>
    <ul class="list-buttons">
      <li v-for="game in games" v-bind:key="game.id">
        <button class="button" @click.stop="chooseGame(game)">
          {{ game.name }} - {{ formatPrice(game) }} SEK
        </button>
      </li>
    </ul>
    <div class="unfinished" v-if="unfinishedTickets.length > 0">
      <h2>Unfinished tickets</h2>
      <ul class="unfinished-list">
        <li
          v-for="ticket in unfinishedTickets"
          v-bind:key="ticket.ticketIdentifier"
        >
          Level {{ ticket.currentLevel > 0 ? ticket.currentLevel : 1 }} - Prize: {{ ticket.prize }} SEK -
          <a href="#" @click.stop="continuePlayingTicket(ticket)">Continue</a>
        </li>
      </ul>
    </div>
    <div class="ticket-buy" v-if="chosenGame != null">
      <div class="bet-amount">
        <label>Bet amount (SEK):</label>
        <input v-model.number="betAmount" type="number" step="0.10" />
      </div>
      <button class="button" @click.stop="buy()">Buy ticket</button>
    </div>
    <div class="ticket-gameplay" v-if="currentGameResult">
      <CashoutGameView
        :gameResult="currentGameResult"
        @continue="handleContinueGame"
        @cashout="handleCashout"
      />
    </div>
  </div>
</template>

<script>
import store from "../store.js";
import {
  updateBalance,
  finalizeGameTicket,
  continueCashoutLevel,
  getUnfinishedTickets,
  finalizeGameWithCorrelationId,
  getCashoutTicketInfo,
} from "../api.js";
import { unifiedBuy } from "../utils/unifiedBuy.js";
import { formatPrice, getSuggestedWager } from "../utils/gameHelper.js";
import CashoutGameView from "./CashoutGameView.vue";

export default {
  name: "CashoutGame",
  components: {
    CashoutGameView,
  },
  props: {},
  mounted() {
    const group = store?.clientConfig?.CashOut ?? [];
    this.games = Array.isArray(group) ? group : [];
  },
  data() {
    return {
      isWaitingForServer: false,
      chosenGame: null,
      unfinishedTickets: [],
      currentGameResult: null,
      betAmount: 2,
      games: [],
      currentTicketId: null,
      currentCorrelationId: null,
    };
  },
  methods: {
    async refreshBalanceWithRetry() {
      for (let i = 0; i < 3; i += 1) {
        const updated = await updateBalance();
        if (updated) {
          return true;
        }

        await new Promise((resolve) => setTimeout(resolve, 250));
      }

      return false;
    },
    formatPrice,
    getSuggestedWager,
    chooseGame(game) {
      this.chosenGame = game;
      this.currentGameResult = null;
      this.betAmount = getSuggestedWager(game) || 2;
      this.checkUnfinishedTickets();
    },
    async checkUnfinishedTickets() {
      if (!this.chosenGame) return;

      try {
        const tickets = await getUnfinishedTickets(this.chosenGame.id);
        if (!Array.isArray(tickets)) {
          this.unfinishedTickets = [];
          return;
        }

        this.unfinishedTickets = await Promise.all(
          tickets.map(async (t) => {
            const first = Array.isArray(t.tickets) && t.tickets[0];
            const ticketIdentifier =
              t.id ||
              first?.id ||
              first?.ticketIdentifier ||
              first?.ticketId ||
              null;

            let currentLevel = 0;
            let prize = first?.prize ?? t.prize ?? 0;

            if (ticketIdentifier) {
              const info = await getCashoutTicketInfo(ticketIdentifier);
              if (info) {
                currentLevel = info.currentLevel ?? 0;
                prize = info.prize ?? prize;
              }
            }

            return {
              ticketIdentifier,
              correlationId: t.correlationId,
              currentLevel,
              prize,
              tickets: t.tickets || null,
            };
          })
        );
      } catch (error) {
        console.error("Error loading unfinished tickets:", error);
        this.unfinishedTickets = [];
      }
    },
    continuePlayingTicket(ticket) {
      this.currentTicketId = ticket.ticketIdentifier;
      this.currentCorrelationId = ticket.correlationId;
      this.currentGameResult = {
        ticketIdentifier: ticket.ticketIdentifier,
        currentLevel: Math.max(1, ticket.currentLevel),
        accumulatedPrize: ticket.prize,
        canContinue: true,
        canCheckout: ticket.currentLevel > 0,
      };
    },
    async buy() {
      if (!this.chosenGame) return;
      await unifiedBuy({
        game: this.chosenGame,
        buyOverrides: {
          wager: this.betAmount,
        },
        setLoading: (v) => {
          this.isWaitingForServer = v;
        },
        refreshTickets: () => this.checkUnfinishedTickets(),
        refreshBalance: () => this.refreshBalanceWithRetry(),
        onSuccess: (data) => {
          if (data?.[0]) {
            const ticket = data[0];
            this.currentTicketId = ticket.id;
            this.currentCorrelationId = ticket.correlationId;
            this.currentGameResult = {
              ticketIdentifier: ticket.id,
              currentLevel: 1,
              accumulatedPrize: this.betAmount,
              canContinue: true,
              canCheckout: false,
            };
          }
        },
        onError: (err) => {
          console.error("Buy error:", err);
        },
      });
    },
    async handleContinueGame() {
      if (!this.currentTicketId) return;

      try {
        this.isWaitingForServer = true;
        const data = await continueCashoutLevel(this.currentTicketId);
        if (data) {
          Object.assign(this.currentGameResult, data);
          if (data.mustFinalize || !data.canContinue) {
            await this.finalizeAndRefresh();
          }
        }
      } catch (error) {
        console.error("Continue error:", error);
      } finally {
        this.isWaitingForServer = false;
      }
    },
    async handleCashout() {
      if (!this.currentTicketId) return;
      try {
        this.isWaitingForServer = true;

        const cashoutResult = await finalizeGameTicket('PUT', 'CashOut', this.currentTicketId);
        if (!cashoutResult) {
          throw new Error("Cashout request failed");
        }

        const finalizeResult = await finalizeGameWithCorrelationId(
          this.currentCorrelationId,
        );
        if (!finalizeResult) {
          throw new Error("Finalize request failed");
        }
        await this.finalizeAndRefresh();
      } catch (error) {
        console.error("Cashout error:", error);
      } finally {
        this.isWaitingForServer = false;
      }
    },
    async finalizeAndRefresh() {
      this.currentGameResult = null;
      this.currentTicketId = null;
      this.currentCorrelationId = null;
      await this.refreshBalanceWithRetry();
      await this.checkUnfinishedTickets();
    },
  },
};
</script>

<style scoped>
.cashout-actions {
  padding: 20px;
}

.overlay-loading {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
}

.list-buttons {
  list-style-type: none;
  padding: 0;
  margin: 10px 0;
}

.list-buttons li {
  margin: 5px 0;
}

.button {
  padding: 10px 20px;
  margin: 5px;
  background-color: var(--color-green);
  color: var(--color-text-dark);
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-weight: bold;
}

.button:hover:not(.disabled) {
  background-color: var(--color-yellow);
}

.button.disabled {
  background-color: var(--color-disabled);
  cursor: not-allowed;
}

.unfinished {
  margin: 20px 0;
  padding: 15px;
  background-color: #f0f0f0;
  border-radius: 5px;
}

.unfinished-list {
  list-style-type: none;
  padding: 0;
}

.unfinished-list li {
  padding: 5px 0;
}

.unfinished-list a {
  color: var(--color-blue);
  text-decoration: none;
  margin-left: 10px;
}

.unfinished-list a:hover {
  text-decoration: underline;
}

.ticket-buy {
  margin: 20px 0;
  padding: 15px;
  background-color: #e8f5e9;
  border-radius: 5px;
}

.bet-amount {
  margin-bottom: 15px;
}

.bet-amount label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
}

.bet-amount input {
  width: 100%;
  max-width: 200px;
  padding: 8px;
  font-size: 14px;
  border: 1px solid var(--color-blue);
  border-radius: 4px;
}

.ticket-gameplay {
  margin: 20px 0;
}
</style>
